import { LocaleService } from 'src/app/services/locale.service';
import { Component, effect, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RssItem } from 'src/app/models/rss';
import { SearchModel, SearchState } from 'src/app/models/search-model';
import { Location } from '@angular/common';
import { Record } from 'src/app/models/record';
import { ApiService } from 'src/app/services/api.service';
import { Util } from 'src/app/util';
import { ConfigService } from 'src/app/services/config.service';
import { paginationState } from './record-pagination-state';

@Component({
  selector: 'app-record-pagination',
  templateUrl: './record-pagination.component.html',
  styleUrls: ['./record-pagination.component.scss'],
})
export class RecordPaginationComponent implements OnInit {
  @Input()
  selectedRssItem: RssItem;

  @Input()
  rssItemSelectedFromUrl: boolean;

  @Input()
  state: SearchState;

  @Input()
  rhId: string;

  @Input()
  showResourceDetails = false;

  @Input()
  record: Record;

  @Input()
  showTopNavigation: boolean;

  showPreviousArrow: boolean;
  showNextArrow: boolean;
  textDirection: string;

  currentPosition: number;

  isLoading: boolean = false;
  readonly publicState = paginationState;

  constructor(
    private router: Router,
    private _location: Location,
    private apiService: ApiService,
    private localeService: LocaleService,
    private configService: ConfigService,
  ) {
    effect(() => {
      this.currentPosition = this.publicState()?.index + 1 + this.publicState()?.page * this.publicState()?.pageSize;
      this.showPreviousArrow = this.currentPosition > 1;
      this.showNextArrow = this.currentPosition < this.publicState()?.total;
      this.isLoading = false;
    });
  }

  ngOnInit() {
    if (this.state) {
      this.currentPosition = this.getCurrentRecordIndex();
      this.showPreviousArrow = this.currentPosition > 1;
      this.showNextArrow = this.currentPosition < this.state.total;
    }
    this.textDirection = this.localeService.getTextDirection();
  }

  goBack() {
    if (this.selectedRssItem != null && !this.rssItemSelectedFromUrl) {
      this.unselectRssItem();
      return;
    }

    if (this.state && this.state.from === 'INIT_SCREEN') {
      return this.router.navigate(['/']);
    }
    if (this.state && this.state.from === 'RESULT_SCREEN' && this.state.genesis) {
      if (!(this.state.genesis instanceof SearchModel)) {
        this.state.genesis = Object.assign(new SearchModel(), this.state.genesis ?? {});
      }
      return this.router.navigate(['/results'], {
        queryParams: this.state.genesis.toRequest(this.state),
      });
    }
    if (this.state && this.state.from === 'LIST_SCREEN' && this.state.listSlug) {
      let listSlug = this.state.listSlug;
      delete this.state.listSlug;
      return this.router.navigate(['/list', listSlug], {
        state: this.state.prevState,
      });
    }
    if (this.state && (this.state.prevNav || this.state.prevNav === '')) {
      return this.router.navigateByUrl(this.state.prevNav ? this.state.prevNav : '/');
    }

    this._location.back();
  }

  unselectRssItem() {
    this.selectedRssItem = null;
    this.rhId = null;
    this.showTopNavigation = this.state && this.state.index !== undefined;
    this._location.replaceState(window.location.pathname.split('?rhId=')[0]);
    this.showResourceDetails = this.shouldShowResourceDetails();
  }

  private shouldShowResourceDetails() {
    return this.selectedRssItem != null || (!this.record.containerData && this.record.resourceTypes.length > 0);
  }

  goToPreviousRecord() {
    this.isLoading = true;

    const prevRecord = this.state.index - 1;
    const searchModel = new SearchModel();
    Object.assign(searchModel, this.state.searchModel);

    this.showPreviousArrow = !(prevRecord <= 0 && searchModel.offset === 0);
    this.showNextArrow = true;

    if (prevRecord < 0) {
      if (searchModel.offset >= searchModel.limit) {
        searchModel.offset = searchModel.offset - searchModel.limit;
      }
      const availability = !(this.configService.isParana() || this.configService.isLeiasp());

      this.apiService.getRecords(searchModel.toRequest(this.state), availability, true).subscribe((records) => {
        this.state.index = this.state.pageSize - 1;

        if (this.state.page > 0) {
          this.state.page = this.state.page - 1;
        }

        this.state.records = records.records?.map((record) => ({
          id: record.id,
          title: record.title,
          wishListInfo: record.wishListInfo,
        }));
        this.state.searchModel = searchModel;
        paginationState.set(this.state);

        const targetRecord = records.records[records.records.length - 1];

        if (!targetRecord.wishListInfo) {
          this.router.navigate(['/info', Util.slugifyRecord(targetRecord)], { state: this.state });
        } else {
          this.router.navigate(['/list', Util.slugifyListRecord(targetRecord)], { state: this.state });
        }
      });
    } else {
      this.state.index = prevRecord;
      paginationState.set(this.state);

      let targetRecord = this.state.records[prevRecord];

      if (!targetRecord.wishListInfo) {
        this.router.navigate(['/info', Util.slugifyRecord(targetRecord)], { state: this.state });
      } else {
        this.router.navigate(['/list', Util.slugifyListRecord(targetRecord)], { state: this.state });
      }
    }
  }

  goToNextRecord() {
    this.isLoading = true;

    const nextRecord = this.state.index + 1;
    const searchModel = new SearchModel();
    Object.assign(searchModel, this.state.searchModel);
    
    this.showPreviousArrow = true;
    this.showNextArrow = nextRecord !== this.state.total - 1;

    if (nextRecord === this.state.pageSize) {
      searchModel.offset = searchModel.offset + searchModel.limit;
      const availability = !(this.configService.isParana() || this.configService.isLeiasp());

      this.apiService.getRecords(searchModel.toRequest(this.state), availability, true).subscribe((records) => {
        this.state.index = 0;
        this.state.page = this.state.page + 1;
        this.state.records = records.records?.map((record) => ({
          id: record.id,
          title: record.title,
          wishListInfo: record.wishListInfo,
        }));
        this.state.searchModel = searchModel;
        const targetRecord = records.records[0];
        paginationState.set(this.state);

        if (!targetRecord.wishListInfo) {
          this.router.navigate(['/info', Util.slugifyRecord(targetRecord)], { state: this.state });
        } else {
          this.router.navigate(['/list', Util.slugifyListRecord(targetRecord)], { state: this.state });
        }
      });
    } else {
      this.state = {
        ...this.state,
        index: nextRecord
      };
      paginationState.set(this.state);

      let targetRecord = this.state.records[nextRecord];

      if (!targetRecord.wishListInfo) {
        this.router.navigate(['/info', Util.slugifyRecord(targetRecord)], { state: this.state });
      } else {
        this.router.navigate(['/list', Util.slugifyListRecord(targetRecord)], { state: this.state });
      }
    }
  }

  private getCurrentRecordIndex() {
    return this.state.index + 1 + this.state.page * this.state.pageSize;
  }
}
